
import React, { useCallback, useMemo } from 'react'
import intl from 'react-intl-universal'
import { Form, Picker, Space, Radio } from 'antd-mobile'
import { RightOutline } from 'antd-mobile-icons'
import { getLanguageStore } from '../../utils/storage/language'
import { useDispatch } from 'react-redux'
import { setOnlinePay } from '../../store/reducers/hotel'
import { replaceCDNDomain } from '../../utils'
// 常量定义
const PAYMENT_TYPES = {
    ONLINE: '1',
    OFFLINE: '2'
}

const RADIO_STYLE = {
    '--font-size': '14px',
    '--icon-size': '18px',
    '--gap': '6px'
}

const PaymentMethodItem = ({ payMethods = {}, onlinePay = {} }) => {

    const dispatch = useDispatch()
    // 获取当前语言
    const currentLanguage = useMemo(() => getLanguageStore(), [])

    // 生成支付方式选项
    const payMethodColumns = useMemo(() => {
        return Object.keys(payMethods).map(key => ({
            label: payMethods[key][currentLanguage] || payMethods[key]['en'] || key,
            value: key,
            item: payMethods[key]
        }))
    }, [payMethods, currentLanguage])

    // 检查是否有可用的支付方式
    const hasOfflinePay = Object.keys(payMethods).length > 0

    /*
    onlinePay = {
        "isEnabled": true,
        "partnerId": "100047",
        "method": {
            "cardspay": {
                "en_US": "cards",
                "zh_CN": "信用卡/借记卡",
                "payMethod": "CARD",
                "image": "http://file.jimutour.com/upload/images/202508/06/20250806172927368.png"
            },
            "alipay": {
                "en_US": "Alipay",
                "zh_CN": "支付宝支付",
                "payMethod": "WEB_PAYMENT",
                "image": "http://file.jimutour.com/upload/images/202508/07/20250807143446314.png"
            },
            "shopeepay": {
                "en_US": "ShopeePay",
                "zh_CN": "ShopeePay",
                "payMethod": "NORMAL_ALL_SP",
                "image": "http://file.jimutour.com/upload/images/202508/07/20250807143531038.png"
            }
        }
    }
    //*/
    let hasOnlinePay = false // 目前在线支付功能未启用
    if (Object.keys(onlinePay).length > 0) {
        hasOnlinePay = onlinePay.isEnabled
    }

    // 渲染线下支付选择器
    const renderOfflinePaymentPicker = useCallback(() => (
        <Form.Item
            name='payMethod'
            label={intl.get('pay_method').d('Payment Method')}
            trigger='onConfirm'
            rules={[{ required: true, message: intl.get('pls_select_pay_method').d('Please select payment method') }]}
            onClick={(e, pickerRef) => {
                pickerRef.current?.open()
            }}
            arrowIcon={<RightOutline color='var(--adm-color-primary)' />}
            className='custom-picker-style'
        >
            <Picker
                cancelText={intl.get('cancel').d('Cancel')}
                confirmText={intl.get('ok').d('OK')}
                columns={[payMethodColumns]}
            >
                {value => {
                    return value?.length > 0
                        ? value[0].label
                        : intl.get('pls_select_pay_method').d('Please select payment method')
                }}
            </Picker>
        </Form.Item>
    ), [payMethodColumns])

    // 渲染支付方式选择
    const renderPaymentTypeSelector = useCallback(() => (
        <Form.Item
            name='payWay'
            initialValue={PAYMENT_TYPES.OFFLINE}
            label={intl.get('payment_type').d('Payment Type')}
            rules={[{ required: true }]}
        >
            <Radio.Group>
                <Space>
                    <Radio
                        value={PAYMENT_TYPES.ONLINE}
                        style={RADIO_STYLE}
                        disabled={!hasOnlinePay}
                    >
                        {intl.get('online_pay').d('Online Payment')}
                    </Radio>
                    <Radio
                        value={PAYMENT_TYPES.OFFLINE}
                        style={RADIO_STYLE}
                        disabled={!hasOfflinePay}
                    >
                        {intl.get('offline_pay').d('Offline Payment')}
                    </Radio>
                </Space>
            </Radio.Group>
        </Form.Item>
    ), [hasOnlinePay, hasOfflinePay])

    // 渲染支付方式内容
    const renderPaymentContent = useCallback(({ payWay }) => {
        if (payWay === PAYMENT_TYPES.ONLINE) {
            const lang = currentLanguage
            return <Form.Item
                name='onlineMethod'
                label={intl.get('pay_method').d('Payment Method')}
                rules={[{ required: true }]}
            >
                <Radio.Group
                    onChange={(value) => {
                        const curObj = onlinePay.method[value]
                        dispatch(setOnlinePay({ ...curObj, partnerId: onlinePay.partnerId }))
                    }}>
                    {
                        Object.keys(onlinePay.method).map(key => {
                            const item = onlinePay.method[key]
                            return (
                                <Space key={key} direction='vertical' style={{ width: '100%', marginBottom: 5 }}>
                                    <Radio style={RADIO_STYLE} value={key} >
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <div className='jm-flex-item' style={{ fontSize: 0 }}>
                                                <img src={replaceCDNDomain(item.image)} style={{ height: 21 }} alt='pay-img' />
                                            </div>
                                            {/* <span>{item[lang] || item['en_US']}</span> */}
                                        </span>
                                    </Radio>
                                </Space>

                            )
                        })
                    }
                </Radio.Group>
            </Form.Item>
        }

        if (payWay === PAYMENT_TYPES.OFFLINE && hasOfflinePay) {
            return renderOfflinePaymentPicker()
        }

        return null
    }, [hasOfflinePay, renderOfflinePaymentPicker])

    // 如果没有可用的支付方式，不渲染组件
    if (!hasOnlinePay && !hasOfflinePay) {
        return null
    }

    // 主要渲染逻辑
    if (hasOnlinePay && hasOfflinePay) {
        return (
            <>
                {renderPaymentTypeSelector()}
                <Form.Subscribe to={['payWay']}>
                    {renderPaymentContent}
                </Form.Subscribe>
            </>
        )
    }

    // 只有线下支付可用
    if (hasOfflinePay) {
        return renderOfflinePaymentPicker()
    }

    // 只有在线支付可用（目前不会执行到这里）
    return <div>{renderPaymentContent({ payWay: '1' })}</div>
}

export default PaymentMethodItem